import { useFormik } from "formik";
import { Button } from "primereact/button";
import { FloatLabel } from "primereact/floatlabel";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { AuthService } from "../../services/authService";
import { Message } from "primereact/message";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Divider } from "primereact/divider";

type Props = {};

const ResetPage = (props: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character")
      .matches(/^\S*$/, "Password must not contain spaces")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const formik = useFormik({
    initialValues: { email: "", password: "", confirmPassword: "" },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        const result = await AuthService.resetPassword(
          values.email,
          values.password
        );
        if (result && result.status === 2000) {
          toastRef.current?.show({
            severity: "success",
            summary: "Success",
            detail: result.message,
            life: 3000,
          });
          navigate("/auth");
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: "Server Error",
            detail: result.message,
            life: 6000,
          });
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred while submitting.";
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
          life: 3000,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const passwordHeader = <div className="font-bold mb-3">Pick a password</div>;
    const passwordFooter = (
      <>
        <Divider />
        <p className="mt-2">Suggestions</p>
        <ul className="pl-2 ml-2 mt-0 line-height-3">
          <li>At least one lowercase</li>
          <li>At least one uppercase</li>
          <li>At least one numeric</li>
          <li>Minimum 8 characters</li>
        </ul>
      </>
    );
  

  useEffect(() => {
    const verifyToken = async () => {
      const token = new URLSearchParams(location.search).get("token");
      setLoading(true);
      try {
        if (token) {
          await AuthService.verifyEmailToken(token)
            .then((res: any) => {
              if (res.statusCode === 2000) {
                toastRef.current?.show({
                  severity: "success",
                  summary: "Success",
                  detail: res.message,
                  life: 3000,
                });
                return;
              }
              if (res.status === -1) {
                toastRef.current?.show({
                  severity: "error",
                  summary: "Server Error",
                  detail: res.message,
                  life: 6000,
                });
                navigate("/auth/forgot-password");
              }
            })
            .catch((err: any) => {
              // console.log(err);
            });
          return;
        }
      } catch (error) {
        toastRef.current?.show({
          severity: "error",
          summary: "Server Error",
          detail: "An error occurred. Please try again later",
          life: 3000,
        });
        navigate("/auth/forgot-password");
      } finally {
        setLoading(false);
      }
      navigate("/auth/forgot-password");
    };
    verifyToken();
  }, [navigate, location.search]);

  return (
    <div className="login mt-5 flex flex-col items-center w-full">
      <div className="mt-5 icon-container mb-6 flex justify-center items-center"></div>
      <Toast ref={toastRef} className="toast-top-center" />

      {loading && (
        <div className="loader-container absolute inset-0 flex justify-center items-center bg-white opacity-50 z-50">
           <ProgressSpinner style={{ width: "50px", height: "50px" }} /> 
        </div>
      )}

      {!loading && ( 
        <>
          <div>
            <h4 className="mt-5 text-2xl text-center mb-1 font-bold">
              Reset your password?
            </h4>
            <p className="mb-5 text-center text-slate-500 text-sm">
              Enter your new password below.
            </p>
          </div>
          <form
            onSubmit={formik.handleSubmit}
            className="w-3/4 flex flex-col items-center"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full md:w-3/4">
              <div className="logI flex flex-col w-3/4 mb-4 col-span-2 mx-auto">
                <FloatLabel>
                  <InputText
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    className={`p-2 border rounded-md w-full ${formik.errors.email && formik.touched.email ? "p-invalid border-red-500" : "border-gray-300"}`}
                  />
                  <label htmlFor="password">Email *</label>
                </FloatLabel>
                {formik.errors.email && formik.touched.email && (
                  <Message
                    severity="error"
                    text={formik.errors.email}
                    className="input-message"
                  />
                )}
              </div>
              <div className="logI flex flex-col w-3/4 mb-4 col-span-2 mx-auto">
                <FloatLabel>
                  <Password
                    id="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    className={`p-2 border rounded-md w-full ${formik.errors.password && formik.touched.password ? "p-invalid border-red-500" : "border-gray-300"}`}
                    toggleMask
                    header={passwordHeader}
                    footer={passwordFooter}
                    showIcon="pi pi-eye"
                    hideIcon="pi pi-eye-slash"
                    feedback={false}
                  />
                  <label htmlFor="password">Password *</label>
                </FloatLabel>
                {formik.touched.password && formik.errors.password && (
                  <Message
                    severity="error"
                    text={formik.errors.password}
                    className="input-message"
                  />
                )}
              </div>
              <div className="logI flex flex-col w-3/4 mb-4 col-span-2 mx-auto">
                <FloatLabel>
                  <Password
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    className={`p-2 border rounded-md w-full ${formik.errors.confirmPassword && formik.touched.confirmPassword ? "p-invalid border-red-500" : "border-gray-300"}`}
                    toggleMask
                    showIcon="pi pi-eye"
                    hideIcon="pi pi-eye-slash"
                    feedback={false}
                  />
                  <label htmlFor="password">Confirm password *</label>
                </FloatLabel>
                {formik.touched.confirmPassword &&
                  formik.errors.confirmPassword && (
                    <Message
                      severity="error"
                      text={formik.errors.confirmPassword}
                      className="input-message"
                    />
                  )}
              </div>
              <div className="logI flex flex-col w-3/4 mb-4 col-span-2 mx-auto">
                <Button
                  label="Confirm"
                  type="submit"
                  loading={submitting}
                  className="w-full col-span-2 p-2 font-bold text-xl rounded bg-primary-500 text-white hover:bg-orange-600"
                  disabled={submitting || !formik.isValid}
                />
              </div>
            </div>
          </form>
          <div className="mt-4 text-center text-sm">
            <p className="mb-0">
              Go to login page?{" "}
              <Link to={"/auth"} className="text-primary-600 fw-semibold">
                Login
              </Link>
            </p>
          </div>
        </>
       )}
    </div>
  );
};

export default ResetPage;
